import { Button } from "@ucl/dse/button";
import { Spinner } from "@ucl/dse/spinner";
import { TextInput } from "@ucl/dse/textInput";
import { SingleSelectInput } from "@ucl/dse/selectInput";
import { Text } from "@ucl/dse/text";
import { ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBoundInput } from "src/hooks/use-bound-input";
import { AddressSelect } from "src/components/address-section/address-select/address-select";
import { withoutRef } from "./helper";
import styles from "./single-address-view.module.scss";

type BaseAddressProps = {
    isFetching: boolean;
    addressOptions: { value: string; label: string }[];
    monthOptions: { value: string; label: string }[];
    yearOptions: { value: string; label: string }[];
    onSelectedAddressChange: (value: { value: string; label: string }) => void;
    onChangeCapture: () => void;
    children?: ReactNode;
    errorKey: string;
    manualAddressToggle: boolean;
    setManualAddressToggle: (value: boolean) => void;
};

type AddressViewProps = BaseAddressProps & {
    addressType: "currentAddress" | "previousAddress";
};

const SingleAddressView = ({
    addressType,
    addressOptions,
    onSelectedAddressChange,
    monthOptions,
    yearOptions,
    isFetching,
    onChangeCapture,
    children,
    errorKey,
    manualAddressToggle,
    setManualAddressToggle,
}: AddressViewProps) => {
    const { t } = useTranslation();
    const backToSearchRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (manualAddressToggle) {
            backToSearchRef.current?.focus();
        }
    }, [manualAddressToggle]);

    const postcodeLookupProps = withoutRef(useBoundInput({ name: `${addressType}.postcodeLookup` }));
    const flatNumberProps = withoutRef(useBoundInput({ name: `${addressType}.flatNumber` }));
    const houseNumberProps = withoutRef(useBoundInput({ name: `${addressType}.houseNumber` }));
    const houseNameProps = withoutRef(useBoundInput({ name: `${addressType}.houseName` }));
    const streetProps = withoutRef(useBoundInput({ name: `${addressType}.street` }));
    const townCityProps = withoutRef(useBoundInput({ name: `${addressType}.townCity` }));
    const countyProps = withoutRef(useBoundInput({ name: `${addressType}.county` }));
    const postcodeProps = withoutRef(useBoundInput({ name: `${addressType}.postcode` }));
    const monthProps = useBoundInput({ name: `${addressType}.month` });
    const yearProps = useBoundInput({ name: `${addressType}.year` });

    return (
        <div data-testid={`${addressType}View`} className={styles.addressViewContainer}>
            {children}
            {!manualAddressToggle && (
                <>
                    <AddressSelect
                        addressType={addressType}
                        postcodeLookupProps={postcodeLookupProps}
                        addressOptions={addressOptions}
                        onSelectedAddressChange={onSelectedAddressChange}
                        manualAddress={manualAddressToggle}
                        setManualAddress={setManualAddressToggle}
                    />
                    {isFetching && <Spinner size="small" />}
                    {errorKey && errorKey != "" && (
                        <Text.P style={{ color: "red", fontSize: "0.875rem" }}>{t(errorKey)}</Text.P>
                    )}
                </>
            )}
            {manualAddressToggle && (
                <div className={styles.backButtonContainer}>
                    <Button
                        ref={backToSearchRef}
                        variant="tertiary"
                        data-testid={"backToAddressSearch"}
                        className={styles.tertiaryButton}
                        onClick={() => setManualAddressToggle(!manualAddressToggle)}
                    >
                        {t("addressDetails.backToAddressSearch")}
                    </Button>
                </div>
            )}
            {manualAddressToggle && (
                <div data-testid={"manualAddressSection"} className={styles.manualAddressSection}>
                    <div className={styles.twoColumnRow}>
                        <div className={styles.flatNumberField}>
                            <TextInput
                                data-testid={"flatNumber"}
                                autoComplete="address-line2"
                                onChangeCapture={onChangeCapture}
                                inputMode={"text"}
                                labelProp={{ label: t("addressDetails.flatNumber") }}
                                placeholder=""
                                maxLength={30}
                                {...flatNumberProps}
                            />
                        </div>
                        <div className={styles.houseNumberField}>
                            <TextInput
                                data-testid={"houseNumber"}
                                autoComplete="address-line1"
                                onChangeCapture={onChangeCapture}
                                inputMode={"text"}
                                labelProp={{ label: t("addressDetails.houseNumber") }}
                                placeholder=""
                                maxLength={10}
                                {...houseNumberProps}
                            />
                        </div>
                    </div>
                    <div className={styles.houseNameField}>
                        <TextInput
                            data-testid={"houseName"}
                            autoComplete="address-line1"
                            onChangeCapture={onChangeCapture}
                            inputMode="text"
                            labelProp={{ label: t("addressDetails.houseName") }}
                            placeholder=""
                            maxLength={50}
                            {...houseNameProps}
                        />
                    </div>
                    <div className={styles.streetField}>
                        <TextInput
                            data-testid={"street"}
                            autoComplete="street-address"
                            inputMode="text"
                            labelProp={{ label: t("addressDetails.street") }}
                            placeholder=""
                            maxLength={60}
                            required={true}
                            {...streetProps}
                        />
                    </div>
                    <div className={styles.twoColumnRow}>
                        <div className={styles.townCityField}>
                            <TextInput
                                data-testid={"townCity"}
                                autoComplete="address-level2"
                                labelProp={{ label: t("addressDetails.townCity") }}
                                inputMode="text"
                                placeholder=""
                                maxLength={30}
                                required={true}
                                {...townCityProps}
                            />
                        </div>
                        <div className={styles.countyField}>
                            <TextInput
                                data-testid={"county"}
                                autoComplete="address-level1"
                                inputMode="text"
                                labelProp={{ label: t("addressDetails.county") }}
                                placeholder=""
                                maxLength={30}
                                required={true}
                                {...countyProps}
                            />
                        </div>
                    </div>
                    <div className={styles.postcodeField}>
                        <TextInput
                            data-testid={"postcode"}
                            autoComplete="postal-code"
                            inputMode="text"
                            labelProp={{ label: t("addressDetails.postcode") }}
                            placeholder=""
                            maxLength={8}
                            required={true}
                            {...postcodeProps}
                        />
                    </div>
                </div>
            )}
            <Text.H2 className={styles.sectionHeading} fontWeight="semi-bold">
                {t("addressDetails.whenDidYouMove")}
            </Text.H2>
            <div className={styles.twoColumnRow}>
                <div className={styles.monthField}>
                    <SingleSelectInput
                        name={monthProps.name}
                        ref={monthProps.ref}
                        onBlur={monthProps.onBlur}
                        inputState={monthProps.inputState}
                        helperTextProp={monthProps.helperTextProp}
                        data-testid="months"
                        options={monthOptions}
                        selected={monthProps.value}
                        onOptionSelect={(option) => {
                            monthProps.onChange(option.value);
                        }}
                        labelProp={{ label: t("addressDetails.month") }}
                        required={true}
                    />
                </div>
                <div className={styles.yearField}>
                    <SingleSelectInput
                        name={yearProps.name}
                        ref={yearProps.ref}
                        onBlur={yearProps.onBlur}
                        inputState={yearProps.inputState}
                        helperTextProp={yearProps.helperTextProp}
                        data-testid="years"
                        options={yearOptions}
                        selected={yearProps.value}
                        onOptionSelect={(option) => {
                            yearProps.onChange(option.value);
                        }}
                        labelProp={{ label: t("addressDetails.year") }}
                        required={true}
                    />
                </div>
            </div>
        </div>
    );
};

export const CurrentAddressView = (props: BaseAddressProps) => {
    const { t } = useTranslation();
    return (
        <SingleAddressView {...props} addressType="currentAddress">
            <Text.H2
                id={"currentAddressHeading"}
                data-testid={"currentAddressHeading"}
                className={styles.sectionHeading}
                fontWeight="semi-bold"
            >
                {t("addressDetails.heading")}
            </Text.H2>
        </SingleAddressView>
    );
};

export const PreviousAddressView = (props: BaseAddressProps) => {
    const { t } = useTranslation();
    return (
        <SingleAddressView {...props} addressType="previousAddress">
            <Text.H2
                id={"previousAddressHeading"}
                data-testid={"previousAddressHeading"}
                className={styles.sectionHeading}
                fontWeight="semi-bold"
            >
                {t("addressDetails.previousAddressHeading")}
            </Text.H2>
        </SingleAddressView>
    );
};
